/**
 * prumo-deep-links.ts · Wave prumo-simple
 *
 * Lookup table: proposalKind + payload fields → concrete deep link the
 * PM can tap once and land on the right screen. No intermediate steps.
 *
 * Resolution order per call:
 *   1. Kind-specific resolver in DEEP_LINK_RESOLVERS (uses payload fields
 *      for max precision).
 *   2. Generic project fallback when projectId is known.
 *   3. `/inbox?focus=<id>` as the universal last-resort.
 *
 * All IDs are URL-encoded. Fragment parts (#diff=<x>) are not encoded —
 * the router and the hash-consumer both expect raw values there.
 *
 * Coverage:
 *   po_approve                → /projects/:id/compras/po/:poId
 *   po_approve (no poId)      → /projects/:id/compras
 *   rdo_draft                 → /projects/:id/rdos/new
 *   rdo_draft (rdoId present) → /projects/:id/rdos/:rdoId
 *   medicao_draft             → /projects/:id/medicoes/:medicaoId  (or /medicoes)
 *   rfq_recommend             → /projects/:id/cotacoes/:quotationId (or /cotacoes)
 *   anomaly_resolve           → /projects/:id/anomalias/:anomalyId (or /anomalias)
 *   budget_change             → /projects/:id/orcamento#diff=:proposalId
 *   budget_overrun            → /projects/:id/orcamento
 *   supplier_followup         → /projects/:id/cotacoes/:quotationId (or /cotacoes)
 *   fvs_overdue               → /projects/:id/qualidade/fvs/:fvsId (or /qualidade/fvs)
 *   rnc_unassigned            → /projects/:id/qualidade/rnc/:rncId (or /qualidade/rnc)
 *   doc_expiring              → /projects/:id/documentos
 *   stage_slip                → /projects/:id/cronograma
 *   cost_variance             → /projects/:id/financeiro
 *   daily_brief               → /inbox
 *   sla_breach                → /projects/:id/rdos
 *   measurement_overdue       → /projects/:id/medicoes
 *   client_update             → /projects/:id/relatorios
 *   quality_rejection         → /projects/:id/qualidade
 *   safety_alert              → /projects/:id/qualidade/rnc
 *   procurement_pending       → /projects/:id/compras
 *   onboarding_step           → /projects/:id/config
 *
 * Fallback: /inbox?focus=<proposalId>
 */

import type { PrumoProposal, PrumoKind } from '@/api/client';
import { getLocale } from '@/i18n';

// ─── Resolver registry ────────────────────────────────────────────────────────

type Resolver = (proposal: PrumoProposal) => string | null;

/** Payload field helpers — typed read from Record<string, unknown>. */
function str(payload: Record<string, unknown>, key: string): string | null {
  const v = payload[key];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

const RESOLVERS: Partial<Record<PrumoKind, Resolver>> & Record<string, Resolver> = {
  // ─ PO approve ─────────────────────────────────────────────────────────────
  po_approve: (p) => {
    if (!p.projectId) return null;
    const poId = str(p.payload, 'poId') ?? str(p.payload, 'po_id') ?? str(p.payload, 'purchaseOrderId');
    if (poId) return `/projects/${enc(p.projectId)}/compras/po/${enc(poId)}`;
    return `/projects/${enc(p.projectId)}/compras`;
  },

  // ─ RDO draft ──────────────────────────────────────────────────────────────
  rdo_draft: (p) => {
    if (!p.projectId) return null;
    const rdoId = str(p.payload, 'rdoId') ?? str(p.payload, 'rdo_id') ?? str(p.payload, 'dailyLogId');
    if (rdoId) return `/projects/${enc(p.projectId)}/rdos/${enc(rdoId)}`;
    return `/projects/${enc(p.projectId)}/rdos/new`;
  },

  // ─ Medição draft ──────────────────────────────────────────────────────────
  medicao_draft: (p) => {
    if (!p.projectId) return null;
    const medicaoId = str(p.payload, 'medicaoId') ?? str(p.payload, 'medicao_id') ?? str(p.payload, 'measurementId');
    if (medicaoId) return `/projects/${enc(p.projectId)}/medicoes/${enc(medicaoId)}`;
    return `/projects/${enc(p.projectId)}/medicoes`;
  },

  // ─ RFQ recommend (cotação) ────────────────────────────────────────────────
  rfq_recommend: (p) => {
    if (!p.projectId) return null;
    const qId = str(p.payload, 'quotationId') ?? str(p.payload, 'rfqId') ?? str(p.payload, 'rfq_id');
    if (qId) return `/projects/${enc(p.projectId)}/cotacoes/${enc(qId)}`;
    return `/projects/${enc(p.projectId)}/cotacoes`;
  },

  // ─ Anomaly resolve ────────────────────────────────────────────────────────
  anomaly_resolve: (p) => {
    if (!p.projectId) return null;
    const aId = str(p.payload, 'anomalyId') ?? str(p.payload, 'anomaly_id') ?? str(p.payload, 'issueId');
    if (aId) return `/projects/${enc(p.projectId)}/anomalias/${enc(aId)}`;
    return `/projects/${enc(p.projectId)}/anomalias`;
  },

  // ─ Extended kinds (present in payload.kind or sources) ───────────────────
  // These are carried as PrumoKind = any-string for extensibility.
  // The resolver receives a proposal whose .kind field matches the key.

  budget_change: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/orcamento#diff=${enc(p.id)}`;
  },

  budget_overrun: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/orcamento`;
  },

  supplier_followup: (p) => {
    if (!p.projectId) return null;
    const qId = str(p.payload, 'quotationId') ?? str(p.payload, 'supplierId');
    if (qId) return `/projects/${enc(p.projectId)}/cotacoes/${enc(qId)}`;
    return `/projects/${enc(p.projectId)}/cotacoes`;
  },

  fvs_overdue: (p) => {
    if (!p.projectId) return null;
    const fvsId = str(p.payload, 'fvsId') ?? str(p.payload, 'fvs_id');
    if (fvsId) return `/projects/${enc(p.projectId)}/qualidade/fvs/${enc(fvsId)}`;
    return `/projects/${enc(p.projectId)}/qualidade/fvs`;
  },

  rnc_unassigned: (p) => {
    if (!p.projectId) return null;
    const rncId = str(p.payload, 'rncId') ?? str(p.payload, 'rnc_id');
    if (rncId) return `/projects/${enc(p.projectId)}/qualidade/rnc/${enc(rncId)}`;
    return `/projects/${enc(p.projectId)}/qualidade/rnc`;
  },

  doc_expiring: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/documentos`;
  },

  stage_slip: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/cronograma`;
  },

  cost_variance: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/financeiro`;
  },

  daily_brief: () => '/inbox',

  sla_breach: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/rdos`;
  },

  measurement_overdue: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/medicoes`;
  },

  client_update: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/relatorios`;
  },

  quality_rejection: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/qualidade`;
  },

  safety_alert: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/qualidade/rnc`;
  },

  procurement_pending: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/compras`;
  },

  onboarding_step: (p) => {
    if (!p.projectId) return null;
    return `/projects/${enc(p.projectId)}/config`;
  },
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Resolves the deep link for a PrumoProposal.
 *
 * Returns a concrete route string suitable for `<Link to={...}>` or
 * `navigate(...)`. Never throws — falls back to /inbox?focus=<id>.
 */
export function resolveDeepLink(proposal: PrumoProposal): string {
  const resolver = RESOLVERS[proposal.kind];
  if (resolver) {
    const result = resolver(proposal);
    if (result) return result;
  }

  // Generic project page fallback when kind isn't in the registry.
  if (proposal.projectId) {
    return `/projects/${enc(proposal.projectId)}`;
  }

  // Universal last resort.
  return `/inbox?focus=${enc(proposal.id)}`;
}

/**
 * Whether the resolved link is a simple inbox focus (least useful).
 * Callers can use this to decide whether to show a "Abrir projeto" label
 * instead of "Fazer agora".
 */
export function isInboxFallback(link: string): boolean {
  return link.startsWith('/inbox?focus=');
}

// ─── Human action label per kind ─────────────────────────────────────────────

const ACTION_LABELS_PT: Partial<Record<PrumoKind, string>> & Record<string, string> = {
  po_approve: 'Aprovar compra',
  rdo_draft: 'Criar RDO',
  medicao_draft: 'Finalizar medição',
  rfq_recommend: 'Ver cotação',
  anomaly_resolve: 'Resolver ponto',
  budget_change: 'Ver diferença',
  budget_overrun: 'Ver orçamento',
  supplier_followup: 'Ver cotação',
  fvs_overdue: 'Concluir FVS',
  rnc_unassigned: 'Atribuir RNC',
  doc_expiring: 'Renovar documento',
  stage_slip: 'Ver cronograma',
  cost_variance: 'Ver financeiro',
  sla_breach: 'Ver diários',
  measurement_overdue: 'Ver medições',
  client_update: 'Enviar relatório',
  quality_rejection: 'Ver qualidade',
  safety_alert: 'Ver não conformidade',
  procurement_pending: 'Ver compras',
  onboarding_step: 'Configurar',
};

const ACTION_LABELS_EN: Partial<Record<PrumoKind, string>> & Record<string, string> = {
  po_approve: 'Approve purchase',
  rdo_draft: 'Create daily log',
  medicao_draft: 'Finalize measurement',
  rfq_recommend: 'View quote',
  anomaly_resolve: 'Resolve issue',
  budget_change: 'View change',
  budget_overrun: 'View budget',
  supplier_followup: 'View quote',
  fvs_overdue: 'Complete inspection',
  rnc_unassigned: 'Assign non-conformance',
  doc_expiring: 'Renew document',
  stage_slip: 'View schedule',
  cost_variance: 'View financials',
  sla_breach: 'View daily logs',
  measurement_overdue: 'View measurements',
  client_update: 'Send report',
  quality_rejection: 'View quality',
  safety_alert: 'View non-conformance',
  procurement_pending: 'View purchases',
  onboarding_step: 'Configure',
};

/**
 * Returns the human action label for the primary CTA button.
 * Falls back to a locale-aware default which is always actionable-sounding.
 */
export function resolveActionLabel(proposal: PrumoProposal): string {
  const isEn = getLocale() === 'en';
  const labels = isEn ? ACTION_LABELS_EN : ACTION_LABELS_PT;
  return labels[proposal.kind] ?? (isEn ? 'Do it now' : 'Fazer agora');
}

/**
 * One-sentence human reason for why this proposal appeared.
 * PM-facing only — no technical terms.
 */
export function resolveWhyPhrase(proposal: PrumoProposal): string {
  const isEn = getLocale() === 'en';
  switch (proposal.kind) {
    case 'po_approve':
      return isEn
        ? 'A purchase order is waiting for your approval.'
        : 'Uma ordem de compra aguarda a sua aprovação.';
    case 'rdo_draft':
      return isEn
        ? "This project's daily log needs to be recorded."
        : 'O diário de obra desta obra precisa ser registrado.';
    case 'medicao_draft':
      return isEn
        ? 'A measurement is in draft and has not been submitted for approval.'
        : 'Uma medição está em rascunho e não foi enviada para aprovação.';
    case 'rfq_recommend':
      return isEn
        ? 'A quote has been flagged that deserves your attention.'
        : 'O sistema identificou uma cotação que merece atenção.';
    case 'anomaly_resolve':
      return isEn
        ? 'An out-of-standard deviation was detected on this project.'
        : 'Um desvio fora do padrão foi identificado nesta obra.';
    default:
      return proposal.subtitle ?? (isEn
        ? 'An item has been flagged that needs your decision.'
        : 'O sistema identificou um ponto que precisa da sua decisão.');
  }
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function enc(s: string): string {
  return encodeURIComponent(s);
}
